import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../Auth/AuthContext";

const CombinedPositionCard = ({
  ticker,
  positions,
  allGainLoss,
  expandedBrokers,
  positionDetails,
  onToggleBrokerExpand,
  onSellFromBroker,
  onAddMore,
  formatCurrency,
  formatQuantity,
}) => {
  const { selectedCurrency, convert, formatMoney, ratesReady, userCurrency } =
    useCurrency();
  const navigate = useNavigate();
  // Calculate totals from new rolling avg function
  const totalQuantity = positions.reduce(
    (sum, p) => sum + parseFloat(p.net_quantity),
    0,
  );
  const totalCost = positions.reduce(
    (sum, p) => sum + parseFloat(p.total_cost),
    0,
  );
  const totalValue = positions.reduce(
    (sum, p) => sum + parseFloat(p.current_value || 0),
    0,
  );
  const totalPL = positions.reduce(
    (sum, p) => sum + parseFloat(p.unrealized_pl || 0),
    0,
  );
  const totalPLPercent = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;
  // Get first position's gainLoss for price data (price is same across brokers)
  const firstGainLoss =
    allGainLoss[`${positions[0].ticker_symbol}-${positions[0].broker_id}`];

  const currency = positions[0].currency;
  const avgPrice = totalQuantity > 0 ? totalCost / totalQuantity : 0;

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1}>
            {/* Combined Header */}
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="1.1rem"
                lineHeight={1.2}
                onClick={() => navigate(`/stocks/${ticker}`)}
                sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
              >
                {ticker}
              </Typography>
              {positions[0].company_name && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  lineHeight={1.2}
                >
                  - {positions[0].company_name}
                </Typography>
              )}
              <Chip
                label={`${positions.length} ${positions.length === 1 ? "broker" : "brokers"}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              {positions[0].exchange && (
                <Chip
                  label={positions[0].exchange}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {firstGainLoss &&
                firstGainLoss.priceChange != null &&
                firstGainLoss.priceChangePercent != null && (
                  <Chip
                    label={`${firstGainLoss.priceChange >= 0 ? "+" : ""}${
                      selectedCurrency === "DEFAULT"
                        ? convert(
                            firstGainLoss.priceChange,
                            positions[0].currency,
                            positions[0].currency,
                          ).toFixed(2)
                        : convert(
                            firstGainLoss.priceChange,
                            positions[0].currency,
                            selectedCurrency,
                          ).toFixed(2)
                    } (${firstGainLoss.priceChangePercent >= 0 ? "+" : ""}${firstGainLoss.priceChangePercent.toFixed(2)}%)`}
                    size="small"
                    color={firstGainLoss.priceChange >= 0 ? "success" : "error"}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
            </Box>

            {/* Combined Stats */}
            <Grid container spacing={1} sx={{ mt: 0 }}>
              <Grid item xs={6} sm={3} md={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  Total Quantity
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  lineHeight={1.3}
                  sx={{ mt: 0.25 }}
                >
                  {formatQuantity(totalQuantity)}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  Avg. Price
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  lineHeight={1.3}
                  sx={{ mt: 0.25 }}
                >
                  {selectedCurrency === "DEFAULT"
                    ? formatMoney(avgPrice, currency, currency)
                    : formatMoney(
                        convert(avgPrice, currency, selectedCurrency),
                        selectedCurrency,
                        selectedCurrency,
                      )}
                </Typography>
              </Grid>

              <>
                <Grid item xs={6} sm={3} md={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    Current Price
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="primary"
                    lineHeight={1.3}
                    sx={{ mt: 0.25 }}
                  >
                    {selectedCurrency === "DEFAULT"
                      ? formatMoney(
                          positions[0].current_value /
                            positions[0].net_quantity,
                          currency,
                          currency,
                        )
                      : formatMoney(
                          convert(
                            positions[0].current_value /
                              positions[0].net_quantity,
                            currency,
                            selectedCurrency,
                          ),
                          selectedCurrency,
                          selectedCurrency,
                        )}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3} md={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    Total Invested
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    lineHeight={1.3}
                    sx={{ mt: 0.25 }}
                  >
                    {selectedCurrency === "DEFAULT"
                      ? formatMoney(totalCost, currency, currency)
                      : formatMoney(
                          convert(totalCost, currency, selectedCurrency),
                          selectedCurrency,
                          selectedCurrency,
                        )}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3} md={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    Total Value
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="primary"
                    lineHeight={1.3}
                    sx={{ mt: 0.25 }}
                  >
                    {selectedCurrency === "DEFAULT"
                      ? formatMoney(totalValue, currency, currency)
                      : formatMoney(
                          convert(totalValue, currency, selectedCurrency),
                          selectedCurrency,
                          selectedCurrency,
                        )}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3} md={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    Total Gain/Loss
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    lineHeight={1.3}
                    sx={{ mt: 0.25 }}
                    color={totalPL >= 0 ? "success.main" : "error.main"}
                  >
                    {totalPL >= 0 ? "+" : ""}
                    {selectedCurrency === "DEFAULT"
                      ? formatMoney(totalPL, currency, currency)
                      : formatMoney(
                          convert(totalPL, currency, selectedCurrency),
                          selectedCurrency,
                          selectedCurrency,
                        )}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                    color={totalPLPercent >= 0 ? "success.main" : "error.main"}
                  >
                    ({totalPLPercent >= 0 ? "+" : ""}
                    {totalPLPercent.toFixed(2)}%)
                  </Typography>
                </Grid>
              </>
            </Grid>

            {/* 52-Week Range - Get from first position's price data */}
            {positions[0].current_value > 0 && (
              <Box mt={0.75} pt={0.75} borderColor="divider">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  52-Week:{" "}
                  {selectedCurrency === "DEFAULT"
                    ? formatMoney(
                        firstGainLoss.fiftyTwoWeekLow,
                        currency,
                        currency,
                      )
                    : formatMoney(
                        convert(
                          firstGainLoss.fiftyTwoWeekLow,
                          currency,
                          selectedCurrency,
                        ),
                        selectedCurrency,
                        selectedCurrency,
                      )}
                  -{" "}
                  {selectedCurrency === "DEFAULT"
                    ? formatMoney(
                        firstGainLoss.fiftyTwoWeekHigh,
                        currency,
                        currency,
                      )
                    : formatMoney(
                        convert(
                          firstGainLoss.fiftyTwoWeekHigh,
                          currency,
                          selectedCurrency,
                        ),
                        selectedCurrency,
                        selectedCurrency,
                      )}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Box
                    flex={1}
                    sx={{
                      height: 4,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        height: "100%",
                        background:
                          "linear-gradient(90deg, #f44336 0%, #ff9800 50%, #4caf50 100%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${firstGainLoss.fiftyTwoWeekPosition}%`,
                        top: -2,
                        width: 3,
                        height: 8,
                        bgcolor: "primary.main",
                        borderRadius: 1,
                        boxShadow: 1,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="primary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    {firstGainLoss.fiftyTwoWeekPosition.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={0.5} ml={2}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddMore}
              sx={{ py: 0.5, px: 1, fontSize: "0.75rem" }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Individual Broker Positions */}
        <Box mt={1}>
          <Divider sx={{ mb: 1 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.7rem"
            fontWeight={600}
            mb={0.75}
            display="block"
          >
            BY BROKER:
          </Typography>
          {positions.map((position, index) => {
            const key = `${position.ticker_symbol}-${position.broker_id}`;
            const gainLoss = allGainLoss[key];
            const isExpanded = expandedBrokers.includes(key);

            return (
              <Box key={key} mb={index < positions.length - 1 ? 0.75 : 0}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={1}
                  bgcolor="grey.50"
                  borderRadius={1}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                  onClick={() => onToggleBrokerExpand(key)}
                >
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        lineHeight={1.3}
                      >
                        {position.broker_name}
                      </Typography>
                      <IconButton size="small" sx={{ p: 0 }}>
                        {isExpanded ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                    >
                      {formatQuantity(position.net_quantity)} @{" "}
                      {selectedCurrency === "DEFAULT"
                        ? formatMoney(position.avg_price, currency, currency)
                        : formatMoney(
                            convert(
                              position.avg_price,
                              currency,
                              selectedCurrency,
                            ),
                            selectedCurrency,
                            selectedCurrency,
                          )}{" "}
                      avg
                      {gainLoss &&
                        ` • Value: ${
                          selectedCurrency === "DEFAULT"
                            ? formatMoney(
                                gainLoss.currentValue,
                                currency,
                                currency,
                              )
                            : formatMoney(
                                convert(
                                  gainLoss.currentValue,
                                  currency,
                                  selectedCurrency,
                                ),
                                selectedCurrency,
                                selectedCurrency,
                              )
                        }`}
                    </Typography>
                  </Box>
                  {gainLoss && (
                    <Box textAlign="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        lineHeight={1.3}
                        color={
                          gainLoss.gainLoss >= 0 ? "success.main" : "error.main"
                        }
                      >
                        {gainLoss.gainLoss >= 0 ? "+" : ""}
                        {selectedCurrency === "DEFAULT"
                          ? formatMoney(gainLoss.gainLoss, currency, currency)
                          : formatMoney(
                              convert(
                                gainLoss.gainLoss,
                                currency,
                                selectedCurrency,
                              ),
                              selectedCurrency,
                              selectedCurrency,
                            )}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontSize="0.7rem"
                        lineHeight={1.1}
                        color={
                          gainLoss.gainLossPercent >= 0
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        ({gainLoss.gainLossPercent >= 0 ? "+" : ""}
                        {gainLoss.gainLossPercent.toFixed(2)}%)
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Transaction Details Collapse */}
                {isExpanded && positionDetails[key] && (
                  <Collapse in={isExpanded}>
                    <Box
                      mt={1}
                      p={2}
                      bgcolor="white"
                      borderRadius={1}
                      border={1}
                      borderColor="grey.200"
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        fontSize="0.85rem"
                      >
                        Transaction History ({positionDetails[key].length}{" "}
                        transactions)
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {positionDetails[key].map((transaction) => {
                            // Calculate total for this transaction
                            const transactionTotal =
                              parseFloat(transaction.quantity) *
                                parseFloat(transaction.purchase_price) +
                              parseFloat(transaction.fees || 0);
                            // Parse date safely
                            let formattedDate = "Invalid Date";
                            try {
                              const date = new Date(transaction.purchase_date);
                              if (!isNaN(date.getTime())) {
                                formattedDate = format(date, "MMM dd, yyyy");
                              }
                            } catch (e) {
                              console.error(
                                "Date parse error:",
                                e,
                                transaction.purchase_date,
                              );
                            }

                            return (
                              <TableRow key={transaction.id}>
                                <TableCell>{formattedDate}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={transaction.transaction_type}
                                    size="small"
                                    color={
                                      transaction.transaction_type === "BUY"
                                        ? "success"
                                        : "error"
                                    }
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {formatQuantity(transaction.quantity)}
                                </TableCell>
                                <TableCell align="right">
                                  {selectedCurrency === "DEFAULT"
                                    ? formatMoney(
                                        transaction.purchase_price,
                                        currency,
                                        currency,
                                      )
                                    : formatMoney(
                                        convert(
                                          transaction.purchase_price,
                                          currency,
                                          selectedCurrency,
                                        ),
                                        selectedCurrency,
                                        selectedCurrency,
                                      )}
                                </TableCell>
                                <TableCell align="right">
                                  {selectedCurrency === "DEFAULT"
                                    ? formatMoney(
                                        transactionTotal,
                                        currency,
                                        currency,
                                      )
                                    : formatMoney(
                                        convert(
                                          transactionTotal,
                                          currency,
                                          selectedCurrency,
                                        ),
                                        selectedCurrency,
                                        selectedCurrency,
                                      )}
                                </TableCell>
                                <TableCell>
                                  {transaction.notes || "-"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CombinedPositionCard;
